import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { Toaster } from "@/components/ui/sonner";

import LoginPage from "@/pages/LoginPage";
import DashboardLayout from "@/components/layout/DashboardLayout";
import DashboardPage from "@/pages/DashboardPage";
import ReturnsListPage from "@/pages/ReturnsListPage";
import ReturnsKanbanPage from "@/pages/ReturnsKanbanPage";
import ReturnDetailPage from "@/pages/ReturnDetailPage";
import StatusesPage from "@/pages/StatusesPage";
import UsersPage from "@/pages/UsersPage";
import AuditLogPage from "@/pages/AuditLogPage";
import ShopifySettingsPage from "@/pages/ShopifySettingsPage";
import PlaceholderModulePage from "@/pages/PlaceholderModulePage";
import PublicReturnFormPage from "@/pages/PublicReturnFormPage";
import PublicServiceFormPage from "@/pages/PublicServiceFormPage";
import ServiceListPage from "@/pages/service/ServiceListPage";
import ServiceKanbanPage from "@/pages/service/ServiceKanbanPage";
import ServiceDetailPage from "@/pages/service/ServiceDetailPage";
import ServiceStatusesPage from "@/pages/service/ServiceStatusesPage";
import ProblemTypesPage from "@/pages/service/ProblemTypesPage";
import OrdersPage from "@/pages/orders/OrdersPage";
import ProductsPage from "@/pages/products/ProductsPage";
import InventoryPage from "@/pages/inventory/InventoryPage";
import AnalyticsPage from "@/pages/analytics/AnalyticsPage";
import DeliveryPage from "@/pages/delivery/DeliveryPage";
import DeliverySettingsPage from "@/pages/delivery/DeliverySettingsPage";
import ExpedierePage from "@/pages/expediere/ExpedierePage";
import BillingPage from "@/pages/billing/BillingPage";
import BillingSettingsPage from "@/pages/billing/BillingSettingsPage";
import AccountingPage from "@/pages/accounting/AccountingPage";
import WarehousePage from "@/pages/warehouse/WarehousePage";
import WarehouseTvPage from "@/pages/warehouse/WarehouseTvPage";
import WarehouseImagesPage from "@/pages/warehouse/WarehouseImagesPage";
import { RealtimeProvider } from "@/lib/realtime-context";

function ProtectedRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">Se încarcă…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" />
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/retur" element={<PublicReturnFormPage />} />
            <Route path="/return" element={<PublicReturnFormPage />} />
            <Route path="/service/public" element={<PublicServiceFormPage />} />
            <Route path="/warehouse-tv" element={<WarehouseTvPage />} />

            {/* Protected dashboard */}
            <Route element={<ProtectedRoute />}>
              <Route
                element={
                  <RealtimeProvider>
                    <DashboardLayout />
                  </RealtimeProvider>
                }
              >
                <Route path="/" element={<DashboardPage />} />
                <Route path="/retururi" element={<ReturnsListPage />} />
                <Route path="/retururi/kanban" element={<ReturnsKanbanPage />} />
                <Route path="/retururi/:id" element={<ReturnDetailPage />} />
                <Route path="/service" element={<ServiceListPage />} />
                <Route path="/service/kanban" element={<ServiceKanbanPage />} />
                <Route path="/service/:id" element={<ServiceDetailPage />} />
                {/* Deprecated modules: WhatsApp + Call Center — redirect to Overview */}
                <Route path="/whatsapp"                element={<Navigate to="/" replace />} />
                <Route path="/whatsapp/*"              element={<Navigate to="/" replace />} />
                <Route path="/call-center"             element={<Navigate to="/" replace />} />
                <Route path="/call-center/*"           element={<Navigate to="/" replace />} />
                <Route path="/setari/call-center"      element={<Navigate to="/" replace />} />
                <Route path="/settings/call-center"    element={<Navigate to="/" replace />} />
                <Route path="/comenzi" element={<OrdersPage />} />
                <Route path="/coletare" element={<WarehousePage />} />
                <Route path="/coletare/imagini" element={<WarehouseImagesPage />} />
                <Route path="/produse" element={<ProductsPage />} />
                <Route path="/stocuri" element={<InventoryPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/livrari" element={<DeliveryPage />} />
                <Route path="/expediere" element={<ExpedierePage />} />
                <Route path="/setari/livrari" element={<DeliverySettingsPage />} />
                <Route path="/facturare" element={<BillingPage />} />
                <Route path="/setari/facturare" element={<BillingSettingsPage />} />
                <Route path="/contabilitate" element={<AccountingPage />} />
                <Route path="/setari/statusuri" element={<StatusesPage />} />
                <Route path="/setari/service-statusuri" element={<ServiceStatusesPage />} />
                <Route path="/setari/tipuri-probleme" element={<ProblemTypesPage />} />
                <Route path="/setari/shopify" element={<ShopifySettingsPage />} />
                <Route path="/setari/utilizatori" element={<UsersPage />} />
                <Route path="/setari/audit" element={<AuditLogPage />} />
                <Route path="/modul/:moduleId" element={<PlaceholderModulePage />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
